import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { CloseOutlined } from "@mui/icons-material";
import Draggable from "react-draggable";
import ButtonComponent from "../../common/ButtonComponent";
import { PiCalculator } from "react-icons/pi";
import useFetchCandidateTokenDetails from "../../hooks/useFetchCandidateTokenDetails";
import { auditLog } from "../../utils/auditLog";

const CalculatorComponent = ({ isMock, dispatch, heartBeat }) => {
  // State declarations
  const [open, setOpen] = useState(false);
  const [display, setDisplay] = useState("");
  const [mode, setMode] = useState(null);
  const [candidateTokenDetails, setCandidateTokenDetails] = useState(null);

  // Derieved variables
  const scientificFunctions = [
    { label: "sin", func: Math.sin },
    { label: "cos", func: Math.cos },
    { label: "tan", func: Math.tan },
    { label: "log", func: Math.log10 },
    { label: "ln", func: Math.log },
    { label: "√", func: Math.sqrt },
    { label: "^", func: (a, b) => Math.pow(a, b) },
    { label: "π", value: Math.PI },
    { label: "e", value: Math.E },
  ];

  //Side effects
  useFetchCandidateTokenDetails({ setCandidateTokenDetails });

  useEffect(() => {
    const calculatorMode = () => {
      if (candidateTokenDetails?.configuration?.calculator === "normal") {
        setMode("normal");
      } else if (
        candidateTokenDetails?.configuration?.calculator === "scientific"
      ) {
        setMode("scientific");
      } else {
        setMode("normal");
      }
    };
    calculatorMode();
  }, [candidateTokenDetails]);

  // Helper functions
  const handleOpen = () => {
    setOpen(true);
    auditLog({
      data: {
        RegistrationNumber: `${candidateTokenDetails?.user?.id}`,
        RollNumber: `${candidateTokenDetails?.user?.rollNo}`,
        Activity: "User selected Calculator",
        IPaddress: "",
      },
      dispatch,
      heartBeat,
    });
  };

  const handleClose = () => setOpen(false);

  const handleClick = (value) => {
    setDisplay((prevDisplay) => prevDisplay + value);
  };

  const handleClear = () => {
    setDisplay("");
  };

  const handleEqual = () => {
    try {
      setDisplay(eval(display).toString());
    } catch {
      setDisplay("Error");
    }
  };

  const handleSingleClear = () => {
    setDisplay((prevDisplay) => prevDisplay.slice(0, -1));
  };

  const openCalculator = () => {
    handleOpen();
  };

  const handleScientificClick = (item) => {
    if (item.func) {
      setDisplay((prevDisplay) => {
        try {
          const result = item.func(parseFloat(prevDisplay));
          return isNaN(result) ? "Error" : result.toString();
        } catch {
          return "Error";
        }
      });
    } else if (item.value) {
      setDisplay((prevDisplay) => prevDisplay + item.value);
    }
  };

  return (
    <>
      <Box style={{ cursor: "pointer" }} onClick={openCalculator}>
        {isMock ? (
          <Button variant="contained" size="small">
            Calculator
          </Button>
        ) : (
          <PiCalculator
            size={15}
            style={{ height: "22px", width: "auto", cursor: "poineter" }}
            title="Calculator"
          />
        )}
      </Box>
      {open && (
        <Draggable handle=".handle">
          <Box
            sx={{
              position: "fixed",
              right: 20,
              bottom: 20,
              width: 400,
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
              borderRadius: "10px",
              zIndex: 9999,
            }}
          >
            <Box
              sx={{ textAlign: "end" }}
              mb={2}
              className="handle"
              style={{ cursor: "move" }}
            >
              {isMock ? (
                <Typography onClick={handleClose}>Close</Typography>
              ) : (
                <ButtonComponent
                  variant="text"
                  onClick={handleClose}
                  borderSquare
                >
                  <CloseOutlined />
                </ButtonComponent>
              )}
            </Box>
            <Container maxWidth="sm">
              <Box>
                <TextField
                  variant="outlined"
                  fullWidth
                  value={display}
                  InputProps={{
                    readOnly: true,
                  }}
                />
                <Grid container spacing={1} sx={{ mt: 1 }}>
                  {mode === "scientific" ? (
                    <>
                      {scientificFunctions.map((item) => (
                        <Grid item xs={4} key={item.label}>
                          <ButtonComponent
                            bgColor="#3f41d1"
                            borderSquare
                            variant="contained"
                            fullWidth
                            onClick={() => handleScientificClick(item)}
                          >
                            {item.label}
                          </ButtonComponent>
                        </Grid>
                      ))}
                      {[
                        "7",
                        "8",
                        "9",
                        "/",
                        "4",
                        "5",
                        "6",
                        "*",
                        "1",
                        "2",
                        "3",
                        "-",
                        "0",
                        ".",
                        "=",
                        "+",
                      ].map((value) => (
                        <Grid item xs={3} key={value}>
                          <ButtonComponent
                            bgColor="#3f41d1"
                            borderSquare
                            variant="contained"
                            fullWidth
                            onClick={() =>
                              value === "=" ? handleEqual() : handleClick(value)
                            }
                          >
                            {value === "*" ? "x" : value}
                          </ButtonComponent>
                        </Grid>
                      ))}
                    </>
                  ) : (
                    <>
                      {[
                        "7",
                        "8",
                        "9",
                        "/",
                        "4",
                        "5",
                        "6",
                        "*",
                        "1",
                        "2",
                        "3",
                        "-",
                        "0",
                        ".",
                        "=",
                        "+",
                      ].map((value) => (
                        <Grid item xs={3} key={value}>
                          <ButtonComponent
                            bgColor="#3f41d1"
                            borderSquare
                            variant="contained"
                            fullWidth
                            onClick={() =>
                              value === "=" ? handleEqual() : handleClick(value)
                            }
                          >
                            {value === "*" ? "x" : value}
                          </ButtonComponent>
                        </Grid>
                      ))}
                    </>
                  )}
                  <Grid item xs={6}>
                    <ButtonComponent
                      bgColor="#3f41d1"
                      borderSquare
                      variant="contained"
                      color="secondary"
                      fullWidth
                      onClick={handleClear}
                    >
                      Clear
                    </ButtonComponent>
                  </Grid>
                  <Grid item xs={6}>
                    <ButtonComponent
                      bgColor="#3f41d1"
                      borderSquare
                      variant="contained"
                      color="secondary"
                      fullWidth
                      onClick={handleSingleClear}
                    >
                      Backspace
                    </ButtonComponent>
                  </Grid>
                </Grid>
              </Box>
            </Container>
          </Box>
        </Draggable>
      )}
      {/* // </Item> */}
    </>
  );
};

export default CalculatorComponent;
